interface SessionTokens {
    accessToken: string;
    refreshToken?: string;
    idToken: string;
}

export interface SessionResponse {
    accessToken: string;
    idToken: string;
    sub?: string;
    email?: string;
    expiresAt?: number;
}

/**
 * Resolve the HttpOnly session cookie into fresh tokens (page-load rehydration).
 * The server transparently refreshes an expired accessToken before answering.
 * Returns null when there is no usable session — caller must treat that as
 * logged out. Unlike the wrappers below this is NOT fire-and-forget.
 */
export async function getSession(baseURL: string): Promise<SessionResponse | null> {
    try {
        const response = await fetch(`${baseURL}/session`, {
            method: 'GET',
            credentials: 'include',
        });
        if (!response.ok) return null;
        return await response.json();
    } catch (e) {
        console.error('getSession failed', e);
        return null;
    }
}

// Fire-and-forget wrappers — auth must not fail if the session endpoint is unavailable.
// baseURL must be the runtime per-tenant API origin (e.g. https://livoniadev.liveline.cloud/customer-portal/api/v1)
// NOT process.env.REACT_APP_SERVER_URL — that env var is dead code and resolves to an internal hostname.

export async function postSession(tokens: SessionTokens, baseURL: string): Promise<void> {
    try {
        await fetch(`${baseURL}/session`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(tokens),
        });
    } catch (e) {
        console.error('postSession failed', e);
    }
}

export async function patchSession(tokens: Partial<SessionTokens>, baseURL: string): Promise<void> {
    try {
        await fetch(`${baseURL}/session`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(tokens),
        });
    } catch (e) {
        console.error('patchSession failed', e);
    }
}

export async function deleteSession(baseURL: string): Promise<void> {
    try {
        await fetch(`${baseURL}/session`, {
            method: 'DELETE',
            credentials: 'include',
        });
    } catch (e) {
        console.error('deleteSession failed', e);
    }
}
